import Switch from '@components/v2/form/Switch/Switch';
import Alert from '@components/v2/ui/Alert/Alert';
import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import Typography from '@components/v2/ui/Typography/Typography';
import { Stack } from '@mui/material';
import { useEffect } from 'react';
import { Control, Controller, UseFormReturn, useWatch } from 'react-hook-form';
import { FeatureFlagEnum } from '../../../../../featureflag/feature-flag.enum';
import LangEnum from '../../../../../lang/LangEnum';
import useGeneralSettingsStore from '../../../../../stores/GeneralSettings/useGeneralSettingsStore';
import useSystemParametersStore from '../../../../../stores/SystemParameters/useSystemParameters.store';
import { useTranslation } from '../../../../translation/TranslationContext';
import { useAbility } from '../../../../users/AbilityContext';
import { ExportOrderForm, ExportType } from '../ExportOrderModal.types';
import { usePdfContentOptions } from '../hooks/usePdfContentOptions';
import {
  FIELD_LABELS,
  PDF_EXPORT_MODELS,
  PDF_EXPORT_TYPE,
  PDF_PAGE_ORIENTATION,
} from '../utils/exportOrder.consts';
import ExportOrderSelectField from './ExportOrderSelectField';

interface PdfContentProps {
  control: Control<ExportOrderForm>;
  asyncExport: boolean;
  formMethods: UseFormReturn<ExportOrderForm>;
}

const TOOLTIP_TRIGGER_SX = {
  fontWeight: 600,
  textDecoration: 'underline',
  cursor: 'help',
} as const;

const LOCKED_OPTION_SX = { fontWeight: 600, cursor: 'default' } as const;

const PdfContent = ({ control, asyncExport, formMethods }: PdfContentProps) => {
  const { groupByOptions, orderByOptions } = usePdfContentOptions({ formValues: formMethods });
  const { setValue } = formMethods;
  const {
    generalSettings: { enableMarkup, enableRetailPrice },
  } = useGeneralSettingsStore();

  const { translate } = useTranslation();
  const ability = useAbility();
  const conditions = ability.getConditions();
  const showMarkupAndPriceBreakdownToggles = !(conditions?.priceCompositionsIds?.length! > 0);
  const { getGlobalSystemParameterValue } = useSystemParametersStore();
  const maxOrderLimit = getGlobalSystemParameterValue(
    FeatureFlagEnum.PDF_ORDER_V2_MAX_ORDERS_LIMIT,
    100,
  );

  const showSellInPrice = useWatch({ control, name: 'showSellInPrice' });
  useEffect(() => {
    if (!showSellInPrice) {
      setValue('showPriceComposition', false);
      setValue('showSizePriceVariation', false);
    }
  }, [showSellInPrice, setValue]);
  const exportType = useWatch({ control, name: 'exportType' });

  const renderPriceOptionLabel = (labelKey: LangEnum) => {
    if (showSellInPrice) {
      return translate(labelKey);
    }

    return (
      <Tooltip TooltipContent={translate(FIELD_LABELS.enableSellInValuesValuesToUnlockThisOption)}>
        <Typography component="span" sx={LOCKED_OPTION_SX}>
          {translate(labelKey)}
        </Typography>
      </Tooltip>
    );
  };

  return (
    <Stack sx={{ gap: 2.5 }}>
      {asyncExport && (
        <Typography variant="body2" color="text.secondary">
          {translate(LangEnum.ORDER_EXPORT_ORDER_MASS_ACTION_INFORMATION)}
        </Typography>
      )}

      <Stack sx={{ gap: 0.75 }}>
        <ExportOrderSelectField
          control={control}
          name="exportType"
          label={translate(FIELD_LABELS.exportType)}
          disabled={!asyncExport}
          options={PDF_EXPORT_TYPE.map(({ labelKey, value }) => ({
            label: translate(labelKey),
            value,
          }))}
        />

        {exportType === ExportType.SINGLE_FILE && (
          <Alert
            severity="info"
            message={LangEnum.ORDER_EXPORT_ORDER_V2_MAX_ORDER_LIMIT}
            values={{ maxOrderLimit }}
          />
        )}

        <Controller
          control={control}
          name="createSummary"
          render={({ field }) => (
            <Switch
              size="medium"
              {...field}
              sx={{ ml: 0 }}
              checked={!!field.value}
              label={
                <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center' }}>
                  <Typography>{translate(FIELD_LABELS.createSummary.summary)}</Typography>

                  <Tooltip TooltipContent={translate(FIELD_LABELS.createSummary.tooltipMessage)}>
                    <Typography
                      component="span"
                      sx={TOOLTIP_TRIGGER_SX}
                      onClick={(event) => event.preventDefault()}
                    >
                      {translate(FIELD_LABELS.createSummary.applicable)}
                    </Typography>
                  </Tooltip>
                </Stack>
              }
            />
          )}
        />
      </Stack>

      <Stack sx={{ gap: 2, pt: 0.5 }}>
        <ExportOrderSelectField
          control={control}
          name="pageOrientation"
          label={translate(FIELD_LABELS.pageOrientation)}
          options={PDF_PAGE_ORIENTATION.map(({ labelKey, value }) => ({
            label: translate(labelKey),
            value,
          }))}
        />

        <ExportOrderSelectField
          control={control}
          name="exportModel"
          label={translate(FIELD_LABELS.exportModel)}
          options={PDF_EXPORT_MODELS.map(({ labelKey, value, description }) => ({
            value,
            label: `${translate(labelKey)} ${translate(description!)}`,
          }))}
        />

        <ExportOrderSelectField
          control={control}
          name="groupBy"
          label={translate(FIELD_LABELS.groupBy)}
          options={groupByOptions.map(({ label, value }) => ({
            label: translate(label),
            value,
          }))}
        />

        <ExportOrderSelectField
          control={control}
          name="sortBy"
          label={translate(FIELD_LABELS.sortBy)}
          options={orderByOptions.map(({ label, value }) => ({
            label: translate(label),
            value,
          }))}
        />
      </Stack>

      <Stack sx={{ gap: 2 }}>
        <Controller
          control={control}
          name="showSellInPrice"
          render={({ field }) => (
            <Switch
              size="medium"
              {...field}
              sx={{ ml: 0 }}
              checked={!!field.value}
              label={translate(FIELD_LABELS.showSellInPrice)}
            />
          )}
        />

        {showMarkupAndPriceBreakdownToggles && (
          <Controller
            control={control}
            name="showPriceComposition"
            render={({ field }) => (
              <Switch
                size="medium"
                {...field}
                sx={{ ml: 0 }}
                checked={!!field.value}
                disabled={!showSellInPrice}
                label={renderPriceOptionLabel(FIELD_LABELS.showPriceComposition)}
              />
            )}
          />
        )}

        <Controller
          control={control}
          name="showSizePriceVariation"
          render={({ field }) => (
            <Switch
              size="medium"
              {...field}
              sx={{ ml: 0 }}
              checked={!!field.value}
              disabled={!showSellInPrice}
              label={renderPriceOptionLabel(FIELD_LABELS.showSizePriceVariation)}
            />
          )}
        />

        {showMarkupAndPriceBreakdownToggles && (enableMarkup || enableRetailPrice) && (
          <Controller
            control={control}
            name="showMarkupAndSellout"
            render={({ field }) => (
              <Switch
                size="medium"
                {...field}
                sx={{ ml: 0 }}
                checked={!!field.value}
                label={translate(FIELD_LABELS.showMarkupAndSellout)}
              />
            )}
          />
        )}
      </Stack>
    </Stack>
  );
};

export default PdfContent;
